import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Loader2,
  Bot,
  Download,
  RefreshCw,
  X,
  AlertTriangle,
  CheckCircle2,
  Filter,
} from 'lucide-react';
import Swal from 'sweetalert2';
import { PageHeader } from '../../components/PageHeader';
import {
  editorialAiService,
  AiReviewRun,
  AiReviewContent,
} from '../../services/editorial-ai.service';
import { portfolioService } from '../../services/portfolio.service';
import { RichText, richTextToPlain } from '../../utils/richTextDisplay';
import TaxonomyCascade from '../../components/TaxonomyCascade';

/**
 * AI Review Monitor (NWFE-RQ-01.4 AC-3) — admin-only.
 * The AI shadow reviewer runs in parallel with the human stages; this view
 * compares the AI's suggested edits with the human's actual edits per task
 * and stage, and exports the divergence dataset (JSONL, AC-7).
 */

const STATUS_BADGE: Record<string, string> = {
  pending: 'bg-gray-50 border-gray-200 text-gray-600',
  running: 'bg-blue-50 border-blue-200 text-blue-700',
  completed: 'bg-green-50 border-green-200 text-green-700',
  failed: 'bg-red-50 border-red-200 text-red-700',
};

const SEVERITY_BADGE: Record<string, string> = {
  critical: 'bg-red-50 border-red-200 text-red-700',
  high: 'bg-orange-50 border-orange-200 text-orange-700',
  medium: 'bg-amber-50 border-amber-200 text-amber-700',
  low: 'bg-gray-50 border-gray-200 text-gray-600',
};

const PAGE_SIZE = 25;

function ContentColumn({ title, content, accent }: { title: string; content?: AiReviewContent; accent?: string }) {
  if (!content) {
    return (
      <div className="flex-1 min-w-0">
        <p className={`text-xs font-bold uppercase mb-2 ${accent || 'text-gray-500'}`}>{title}</p>
        <p className="text-xs text-gray-400 italic">Not available yet</p>
      </div>
    );
  }
  return (
    <div className="flex-1 min-w-0">
      <p className={`text-xs font-bold uppercase mb-2 ${accent || 'text-gray-500'}`}>{title}</p>
      <div className="space-y-2 text-sm">
        <div className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-0.5">Stem</p>
          <p className="text-gray-800 whitespace-pre-wrap">{content.stem ? <RichText value={content.stem} /> : '—'}</p>
        </div>
        {(content.alternatives || []).map((alt) => (
          <div key={alt.letter} className="bg-gray-50 rounded-lg p-2 flex gap-2">
            <span className="text-xs font-bold text-gray-500">{alt.letter})</span>
            <p className="text-gray-700 text-xs whitespace-pre-wrap flex-1"><RichText value={alt.text} /></p>
          </div>
        ))}
        {content.correctAnswer && (
          <div className="bg-green-50 rounded-lg p-2.5 border border-green-100">
            <p className="text-[10px] font-semibold text-green-600 uppercase mb-0.5">Correct answer</p>
            <p className="text-gray-800 text-xs whitespace-pre-wrap"><RichText value={content.correctAnswer} /></p>
          </div>
        )}
        <div className="bg-gray-50 rounded-lg p-2.5">
          <p className="text-[10px] font-semibold text-gray-400 uppercase mb-0.5">Explanation</p>
          <p className="text-gray-700 text-xs whitespace-pre-wrap">{content.explanation ? <RichText value={content.explanation} /> : '—'}</p>
        </div>
      </div>
    </div>
  );
}

export function AIReviewMonitor() {
  const queryClient = useQueryClient();
  const [stageFilter, setStageFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<AiReviewRun | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  // Client request: text / portfolio / date filters + Question Bank base-scope
  // parity (OE / type / difficulty / taxonomy — Lucas)
  const [searchFilter, setSearchFilter] = useState('');
  const [portfolioFilter, setPortfolioFilter] = useState('all');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [oeFilter, setOeFilter] = useState('');
  const [appliedOe, setAppliedOe] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [difficultyFilter, setDifficultyFilter] = useState('');
  const [taxonomyNodeFilter, setTaxonomyNodeFilter] = useState<string | null>(null);

  const buildFilters = () => ({
    stage: stageFilter ? parseInt(stageFilter, 10) : undefined,
    status: statusFilter || undefined,
    search: appliedSearch || undefined,
    portfolio: portfolioFilter !== 'all' ? portfolioFilter : undefined,
    from: fromDate || undefined,
    to: toDate ? `${toDate}T23:59:59.999Z` : undefined,
    oeCode: appliedOe || undefined,
    type: typeFilter || undefined,
    difficulty: difficultyFilter || undefined,
    taxonomyNode: taxonomyNodeFilter || undefined,
  });

  const { data: portfolios = [] } = useQuery({
    queryKey: ['portfolios'],
    queryFn: () => portfolioService.list(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const monitorPortfolioId = portfolios.find((p) => p.name === portfolioFilter)?._id || null;

  const { data, isLoading, error } = useQuery({
    queryKey: ['ai-reviews', stageFilter, statusFilter, appliedSearch, portfolioFilter, fromDate, toDate, appliedOe, typeFilter, difficultyFilter, taxonomyNodeFilter, page],
    queryFn: () =>
      editorialAiService.listReviews({
        ...buildFilters(),
        page,
        limit: PAGE_SIZE,
      }),
  });

  // Client request: export as XLSX / CSV / JSONL, honoring current filters
  const handleExport = async () => {
    const { value: format } = await Swal.fire({
      title: 'Export AI reviews',
      input: 'radio',
      inputOptions: { xlsx: 'Excel (XLSX)', csv: 'CSV', jsonl: 'JSONL (training dataset)' },
      inputValue: 'xlsx',
      html: '<p style="font-size:13px;color:#555">The export honors the filters currently applied (stage, status, text, portfolio, date range).</p>',
      showCancelButton: true,
      confirmButtonText: 'Export',
      confirmButtonColor: '#1e73be',
      inputValidator: (v) => (!v ? 'Choose a format' : undefined),
    });
    if (!format) return;

    setIsExporting(true);
    try {
      const blob = await editorialAiService.exportReviews(buildFilters(), format as 'jsonl' | 'csv' | 'xlsx');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ai-reviews-${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      Swal.fire({
        title: 'Export failed',
        text: err.response?.data?.error || 'Could not download the export.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    } finally {
      setIsExporting(false);
    }
  };

  const handleRerun = async (run: AiReviewRun) => {
    try {
      await editorialAiService.rerun(run.taskId, run.stage);
      queryClient.invalidateQueries({ queryKey: ['ai-reviews'] });
      Swal.fire({
        title: 'Queued',
        text: `Stage ${run.stage} AI review re-queued.`,
        icon: 'success',
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (err: any) {
      Swal.fire({
        title: 'Error',
        text: err.response?.data?.error || 'Could not queue the review.',
        icon: 'error',
        confirmButtonColor: '#dc2626',
      });
    }
  };

  const reviews = data?.reviews || [];
  const summary = data?.summary || {};
  const pagination = data?.pagination;
  const totalPages = pagination?.totalPages || 1;
  const completed = summary['completed'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <PageHeader
          title="AI Review Monitor"
          description="Shadow AI reviewer vs human reviewers — divergence tracking per stage (admin only)"
          breadcrumbs={[{ label: 'Editorial Review' }, { label: 'AI Review Monitor' }]}
        />
        <div className="flex items-center gap-3">
          <button
            onClick={async () => {
              try {
                const result = await editorialAiService.requeueMissing();
                queryClient.invalidateQueries({ queryKey: ['ai-reviews'] });
                Swal.fire({
                  title: 'Queued',
                  text: `Stage 1: ${result.queuedStage1} · Stage 2: ${result.queuedStage2} review(s) queued.`,
                  icon: 'success',
                  confirmButtonColor: '#1e73be',
                });
              } catch (err: any) {
                Swal.fire({
                  title: 'Error',
                  text: err.response?.data?.error || 'Could not queue missing reviews.',
                  icon: 'error',
                  confirmButtonColor: '#dc2626',
                });
              }
            }}
            className="px-5 py-3 rounded-xl border border-[#1e73be] text-[#1e73be] font-semibold hover:bg-blue-50 transition-colors flex items-center gap-2"
            title="Queue AI reviews for pending tasks that have no run yet"
          >
            <RefreshCw className="w-4 h-4" />
            Queue missing
          </button>
          <button
            onClick={async () => {
              try {
                const result = await editorialAiService.requeueStale();
                queryClient.invalidateQueries({ queryKey: ['ai-reviews'] });
                Swal.fire({
                  title: 'Re-queued',
                  text: `${result.requeued} of ${result.scanned} incomplete review(s) re-queued. Refresh in a moment to see updated results.`,
                  icon: 'success',
                  confirmButtonColor: '#1e73be',
                });
              } catch (err: any) {
                Swal.fire({
                  title: 'Error',
                  text: err.response?.data?.error || 'Could not re-queue stale reviews.',
                  icon: 'error',
                  confirmButtonColor: '#dc2626',
                });
              }
            }}
            className="px-5 py-3 rounded-xl border border-amber-400 text-amber-700 font-semibold hover:bg-amber-50 transition-colors flex items-center gap-2"
            title="Re-run reviews whose stored content is incomplete (captured before the fix)"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh stale
          </button>
          <button
            onClick={handleExport}
            disabled={isExporting}
            className="px-6 py-3 bg-gradient-to-r from-[#1e73be] to-[#4A90C8] hover:from-[#1e73be]/90 hover:to-[#4A90C8]/90 text-white rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50"
            title="Export the AI-vs-human divergence dataset (JSONL)"
          >
            {isExporting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
            Export (XLSX / CSV / JSONL)
          </button>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Completed runs</p>
          <p className="text-2xl font-bold text-gray-800">{completed?.count || 0}</p>
        </div>
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Avg agreement with human</p>
          <p className="text-2xl font-bold text-[#1e73be]">
            {completed?.avgAgreement != null ? `${Math.round(completed.avgAgreement * 100)}%` : '—'}
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Avg processing time</p>
          <p className="text-2xl font-bold text-gray-800">
            {completed?.avgTimeMs != null ? `${(completed.avgTimeMs / 1000).toFixed(1)}s` : '—'}
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4">
          <p className="text-xs text-gray-500 mb-1">Failed runs</p>
          <p className="text-2xl font-bold text-red-600">{summary['failed']?.count || 0}</p>
        </div>
      </div>

      {/* Filters (client request: text / portfolio / date added) */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex items-center gap-2 pb-2">
            <Filter className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-semibold text-gray-700">Filters</span>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Stage</label>
            <select
              value={stageFilter}
              onChange={(e) => { setStageFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:border-[#1e73be] outline-none"
            >
              <option value="">All stages</option>
              <option value="1">Stage 1 — Linguistic</option>
              <option value="2">Stage 2 — Pedagogical</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:border-[#1e73be] outline-none"
            >
              <option value="">All statuses</option>
              <option value="completed">Completed</option>
              <option value="running">Running</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Portfolio</label>
            <select
              value={portfolioFilter}
              onChange={(e) => { setPortfolioFilter(e.target.value); setTaxonomyNodeFilter(null); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:border-[#1e73be] outline-none"
            >
              <option value="all">All portfolios</option>
              {portfolios.map((p) => (
                <option key={p._id} value={p.name}>{p.name}</option>
              ))}
            </select>
            {monitorPortfolioId && (
              <div className="mt-1 max-w-[420px]">
                <TaxonomyCascade
                  portfolioId={monitorPortfolioId}
                  value={taxonomyNodeFilter}
                  onChange={(nodeId) => { setTaxonomyNodeFilter(nodeId); setPage(1); }}
                  compact
                />
              </div>
            )}
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">OE Code</label>
            <input
              type="text"
              value={oeFilter}
              onChange={(e) => setOeFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { setAppliedOe(oeFilter); setPage(1); } }}
              onBlur={() => { setAppliedOe(oeFilter); setPage(1); }}
              placeholder="e.g. 408"
              className="w-28 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Type</label>
            <select
              value={typeFilter}
              onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:border-[#1e73be] outline-none"
            >
              <option value="">All types</option>
              <option value="multiple_choice">Multiple choice</option>
              <option value="short_answer">Short answer</option>
              <option value="case_study">Case study</option>
              <option value="interactive">Interactive</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Difficulty</label>
            <select
              value={difficultyFilter}
              onChange={(e) => { setDifficultyFilter(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm bg-white focus:border-[#1e73be] outline-none"
            >
              <option value="">All</option>
              <option value="easy">Easy</option>
              <option value="medium">Medium</option>
              <option value="hard">Hard</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">From</label>
            <input type="date" value={fromDate}
              onChange={(e) => { setFromDate(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">To</label>
            <input type="date" value={toDate}
              onChange={(e) => { setToDate(e.target.value); setPage(1); }}
              className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none" />
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-xs text-gray-500 mb-1">Search (question / issues)</label>
            <div className="flex gap-2">
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { setAppliedSearch(searchFilter); setPage(1); } }}
                placeholder="Free text..."
                className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#1e73be] outline-none"
              />
              <button
                onClick={() => { setAppliedSearch(searchFilter); setPage(1); }}
                className="px-4 py-2 bg-[#1e73be] hover:bg-[#1e73be]/90 text-white rounded-lg text-sm font-semibold"
              >
                Apply
              </button>
              {(appliedSearch || portfolioFilter !== 'all' || fromDate || toDate || stageFilter || statusFilter || appliedOe || typeFilter || difficultyFilter || taxonomyNodeFilter) && (
                <button
                  onClick={() => {
                    setSearchFilter(''); setAppliedSearch(''); setPortfolioFilter('all');
                    setFromDate(''); setToDate(''); setStageFilter(''); setStatusFilter('');
                    setOeFilter(''); setAppliedOe(''); setTypeFilter(''); setDifficultyFilter(''); setTaxonomyNodeFilter(null);
                    setPage(1);
                  }}
                  className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-lg text-sm font-semibold"
                >
                  Clear
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl shadow-md border border-gray-100 overflow-hidden">
        {error ? (
          <div className="p-10 text-center text-sm text-red-500">
            {(error as any)?.response?.status === 403
              ? 'You do not have access to AI review monitoring.'
              : (error as any)?.response?.data?.error || 'Failed to load AI reviews.'}
          </div>
        ) : isLoading ? (
          <div className="p-10 flex items-center justify-center gap-2 text-gray-500">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="text-sm">Loading AI reviews...</span>
          </div>
        ) : reviews.length === 0 ? (
          <div className="p-10 text-center">
            <Bot className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              No AI reviews yet. Runs are queued automatically when questions enter Stage 1 or Stage 2.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                  <th className="px-4 py-3">Question</th>
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Stage / Subagent</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Issues</th>
                  <th className="px-4 py-3">Agreement</th>
                  <th className="px-4 py-3">Time</th>
                  <th className="px-4 py-3">Prompt</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {reviews.map((run) => (
                  <tr
                    key={run._id}
                    className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelected(run)}
                  >
                    <td className="px-4 py-3 max-w-[240px]">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {run.question?.oeCode && (
                          <span className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-bold text-blue-700 whitespace-nowrap">
                            {run.question.oeCode}
                          </span>
                        )}
                        {run.question?.type && (
                          <span className="px-1.5 py-0.5 rounded bg-gray-50 border border-gray-200 text-[10px] font-semibold text-gray-600 whitespace-nowrap">
                            {run.question.type}
                          </span>
                        )}
                        {run.question?.difficulty && (
                          <span className="px-1.5 py-0.5 rounded bg-amber-50 border border-amber-200 text-[10px] font-semibold text-amber-700 whitespace-nowrap">
                            {run.question.difficulty}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-700 line-clamp-2 mt-0.5" title={richTextToPlain(run.question?.stemPreview)}>
                        {richTextToPlain(run.question?.stemPreview) || '—'}
                      </p>
                      <p className="text-[10px] text-gray-400 font-mono mt-0.5">
                        ID: {run.question?._id || run.questionId}
                      </p>
                      {/* Client/request identity: portfolio + taxonomy chips */}
                      {((run.portfolios || []).length > 0 || (run.question?.taxonomyPaths || []).length > 0) && (
                        <div className="flex items-center gap-1 flex-wrap mt-1">
                          {(run.portfolios || []).map((pf: string) => (
                            <span key={pf} className="inline-flex px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-semibold text-blue-700">
                              {pf}
                            </span>
                          ))}
                          {(run.question?.taxonomyPaths || []).map((path: string) => (
                            <span key={path} className="inline-flex px-1.5 py-0.5 rounded bg-purple-50 border border-purple-200 text-[10px] font-semibold text-purple-700">
                              {path}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-xs">
                      {new Date(run.createdAt).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="font-semibold text-gray-700">Stage {run.stage}</span>
                      <div className="text-xs text-gray-400 capitalize">{run.subagent}</div>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 rounded-lg border text-xs font-semibold ${STATUS_BADGE[run.status]}`}>
                        {run.status}
                      </span>
                      {run.error && (
                        <div className="text-xs text-red-500 mt-1 max-w-[160px] truncate" title={run.error}>
                          {run.error}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-700">
                      {run.status === 'completed' ? run.issues.length : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      {run.divergence ? (
                        <span className={`font-bold ${run.divergence.agreementRate >= 0.7 ? 'text-green-600' : 'text-amber-600'}`}>
                          {Math.round(run.divergence.agreementRate * 100)}%
                        </span>
                      ) : (
                        <span className="text-gray-400 text-xs">awaiting human</span>
                      )}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-gray-600 text-xs">
                      {run.processingTimeMs ? `${(run.processingTimeMs / 1000).toFixed(1)}s` : '—'}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-xs text-gray-500">
                      {run.promptSource?.source || '—'}
                      {run.promptSource?.version ? ` v${run.promptSource.version}` : ''}
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRerun(run);
                        }}
                        className="p-2 rounded-lg text-gray-500 hover:text-[#1e73be] hover:bg-blue-50 transition-colors"
                        title="Re-run AI review"
                      >
                        <RefreshCw className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {!error && !isLoading && (pagination?.total || 0) > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-500">
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, pagination!.total)} of {pagination!.total}
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40"
              >
                Previous
              </button>
              <span className="text-xs text-gray-500">Page {page} of {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 text-xs font-semibold text-gray-600 hover:bg-gray-100 disabled:opacity-40"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Detail modal — AI vs human side-by-side (AC-3) */}
      {selected && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-6">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                  <Bot className="w-5 h-5 text-[#1e73be]" />
                  Stage {selected.stage} — {selected.subagent} review
                </h2>
                <p className="text-xs text-gray-600 mt-0.5">
                  {selected.question?.oeCode && (
                    <span className="font-semibold text-blue-700">{selected.question.oeCode} · </span>
                  )}
                  {selected.question?.stemPreview}
                  <span className="text-gray-400 font-mono"> · ID: {selected.question?._id || selected.questionId}</span>
                </p>
                <p className="text-xs text-gray-500">
                  {selected.llmModel ? `Model: ${selected.llmModel} · ` : ''}
                  Prompt: {selected.promptSource?.source || 'default'}
                  {selected.promptSource?.version ? ` v${selected.promptSource.version}` : ''}
                  {selected.divergence
                    ? ` · Agreement with human: ${Math.round(selected.divergence.agreementRate * 100)}%`
                    : ''}
                </p>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="p-2 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {selected.summary && (
              <div className="mb-4 rounded-xl bg-blue-50 border border-blue-200 p-3 text-sm text-blue-800">
                {selected.summary}
              </div>
            )}

            {/* Issues */}
            {selected.issues.length > 0 && (
              <div className="mb-5">
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">
                  Identified issues ({selected.issues.length})
                </p>
                <div className="space-y-1.5">
                  {selected.issues.map((issue, idx) => (
                    <div key={idx} className="flex items-start gap-2 rounded-lg border border-gray-100 bg-gray-50 p-2.5">
                      <AlertTriangle className={`w-4 h-4 flex-shrink-0 mt-0.5 ${issue.severity === 'critical' || issue.severity === 'high' ? 'text-red-500' : 'text-amber-500'}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5 mb-0.5">
                          {issue.ruleRef && (
                            <span className="px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200 text-[10px] font-bold text-blue-700">
                              {issue.ruleRef}
                            </span>
                          )}
                          <span className={`px-1.5 py-0.5 rounded border text-[10px] font-semibold ${SEVERITY_BADGE[issue.severity]}`}>
                            {issue.severity}
                          </span>
                          <span className="text-[10px] text-gray-400">
                            {issue.source} · confidence {Math.round(issue.confidence * 100)}%
                          </span>
                        </div>
                        <p className="text-xs text-gray-700">{issue.description}</p>
                        {issue.suggestion && (
                          <p className="text-xs text-green-700 mt-0.5 flex items-start gap-1">
                            <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" /> {issue.suggestion}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Three-way comparison */}
            <div className="flex gap-4 flex-col lg:flex-row">
              <ContentColumn title="Input (stage entry)" content={selected.inputContent} />
              <ContentColumn title="AI revised (fixes applied)" content={selected.suggestedContent} accent="text-[#1e73be]" />
              <ContentColumn title="Human final" content={selected.humanFinalContent} accent="text-green-700" />
            </div>

            {/* Field-level divergence */}
            {selected.divergence && (
              <div className="mt-5">
                <p className="text-xs font-bold text-gray-500 uppercase mb-2">Field divergence</p>
                <div className="flex flex-wrap gap-1.5">
                  {Object.entries(selected.divergence.fields).map(([field, d]) => (
                    <span
                      key={field}
                      className={`px-2 py-1 rounded-lg border text-xs font-semibold ${
                        d.aiEqualsHuman
                          ? 'bg-green-50 border-green-200 text-green-700'
                          : 'bg-amber-50 border-amber-200 text-amber-700'
                      }`}
                      title={`AI changed: ${d.aiChanged} · Human changed: ${d.humanChanged}`}
                    >
                      {field}: {d.aiEqualsHuman ? 'match' : 'diverged'}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default AIReviewMonitor;
